import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import { APP_STORE } from '@core/stores/app.store';
import { TemplatingPipe } from '@shared/pipes/templating.pipe';
import { Notification } from '@notifications/models/notification';
import { NotificationMessage } from '@notifications/models/notification-message';
import { NotificationStatus } from '@notifications/models/notification-status';
import { NotificationEntitiesService } from '@notifications/services/notification-entities.service';
import { ToastNotificationsService } from '@notifications/services/toast-notifications.service';

interface NotificationsState {
  entities: Notification[];
  isSidenavOpened: boolean;
}

const initialState: NotificationsState = {
  entities: [],
  isSidenavOpened: false
};

export const NOTIFICATIONS_STORE = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      appStore = inject(APP_STORE),
      toastNotificationsService = inject(ToastNotificationsService),
      notificationEntitiesService = inject(NotificationEntitiesService),
      templatingPipe = inject(TemplatingPipe)
    ) => ({
      toggleNotificationsSidenav(): void {
        patchState(store, {
          isSidenavOpened: !store.isSidenavOpened()
        });
      },

      addOrUpdateNotification(notification: Notification): void {
        const notificationAlreadyExists = store
          .entities()
          .some(x => x.id === notification.id);

        const entities = notificationAlreadyExists
          ? store
              .entities()
              .map(x => (x.id === notification.id ? notification : x))
          : [notification, ...store.entities()];

        patchState(store, { entities });

        const selectedClientId = appStore.selectedClientId();
        const selectedProjectId = appStore.selectedProjectId();
        const shouldShowToast =
          !notification.skipToastNotification &&
          notification.clientId === selectedClientId &&
          notification.projectId === selectedProjectId;

        if (!shouldShowToast) {
          return;
        }

        const notificationMessage = getNotificationMessage(
          notification,
          notificationEntitiesService,
          templatingPipe
        );
        const { message, title } = notificationMessage;

        switch (notification.status) {
          case NotificationStatus.InProgress:
            toastNotificationsService.info(message, title);
            break;
          case NotificationStatus.Completed:
            toastNotificationsService.success(message, title);
            break;
          case NotificationStatus.Failed:
            toastNotificationsService.error(message, title);
            break;
        }
      },

      removeNotification(notificationId: number): void {
        patchState(store, {
          entities: store.entities().filter(x => x.id !== notificationId)
        });
      }
    })
  )
);

const getNotificationMessage = (
  notification: Notification,
  notificationEntitiesService: NotificationEntitiesService,
  templatingPipe: TemplatingPipe
): NotificationMessage => {
  const template: NotificationMessage = {
    ...notificationEntitiesService.entities[notification.type].messages[
      notification.status
    ]
  };

  template.message = templatingPipe.transform(
    template.message,
    notification.templateVariables
  );

  return template;
};
